import { assertCategoriesAvailable } from '../../../services/resourceLifecycle';
import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { collection, getDocs, doc, getDoc, updateDoc, deleteDoc, runTransaction, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../services/firebase';
import { assertTableAcceptsWrites } from '../../../services/tableWrites';
import TagSelector from './TagSelector';
import ImportDataProfilesDialog from './ImportDataProfilesDialog';
import HelpButton from '../../common/HelpButton';
import { formatText } from '../../common/TextFormatterProvider';
// Aliased: the local state variable is also called `profileName`.
import { profileName as getProfileName, normalizeProfileName, profileNameWinner } from '../../../utils/profileDisplay';
import { missingMandatoryAttributes, tagsToValueMap } from '../../../utils/mandatoryAttributes';
import { reservedNameError } from '../../../utils/tagValueUtils';

// What the form holds, as one comparable string: the draft is dirty when this
// differs from what the editor opened with, and a stored profile has changed
// since then when ITS signature differs from that baseline. Every default
// attribute takes part, in a fixed order, so one added, removed or given
// another value counts as a change.
const draftSignature = (name, tags, prompt) => JSON.stringify([
  name,
  [...tags].map((tag) => [tag.category, tag.value]).sort((a, b) => String(a[0]).localeCompare(String(b[0]))),
  prompt,
]);

// The form a stored profile opens as: its name, every default attribute, its prompt.
const formOf = (profile) => ({
  name: getProfileName(profile),
  tags: Object.entries(profile?.default_tags || {}).map(([category, value]) => ({ category, value })),
  prompt: profile?.extraction_prompt || '',
});

const CHANGED_SINCE_OPEN = 'This data profile has changed since you opened it. '
  + 'Please re-edit if you wish to continue with the change.';

// Copying a profile carries over its learned stores and nothing else: `file_stats`,
// `total_cleaning_cost_usd`, `status`, `deleted_at` and `name` are deliberately never
// copied — the copy starts with its own name, Active status and empty statistics.
const COPIED_LEARNED_FIELDS = [
  'possible_values',
  'record_schemas',
  'extraction_corrections',
  'disposition_signatures',
];

const DataProfileEditor = ({ open, onClose, table, initialProfile, copySource, companyId, tableId }) => {
  const [profileName, setProfileName] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [extractionPrompt, setExtractionPrompt] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  // The form as it opened; closing with a draft that differs asks first.
  const baselineRef = useRef(draftSignature('', [], ''));
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const isEditing = Boolean(initialProfile);
  // Copy mode creates a new profile pre-filled from an existing one.
  const isCopying = Boolean(copySource) && !isEditing;

  // Extract metadata columns from the table prop
  const attributeColumns = table?.attributeColumns || [];

  // The profile as handed in on this render. The seed below reads it through
  // this ref so that it can key on the OPENING — the dialog and the profile's
  // id — rather than on the object: the dashboard hands in the live selection,
  // rebuilt on every snapshot of the profiles collection (an upload's
  // statistics landing, another tab's save), and re-seeding on that identity
  // replaced the user's draft mid-typing (M231).
  const openedWithRef = useRef({ initialProfile, copySource });
  openedWithRef.current = { initialProfile, copySource };
  const openedProfileId = initialProfile?.id ?? null;
  const copySourceId = copySource?.id ?? null;

  useEffect(() => {
    if (!open) return;
    // What the form opens with — set as state, and remembered as the baseline
    // the dirty check and the save-time stale check compare against.
    const { initialProfile: opening, copySource: source } = openedWithRef.current;
    let form = { name: '', tags: [], prompt: '' };
    if (opening) {
      form = formOf(opening);
    } else if (source) {
      // Copy mode — pre-fill from the source; the name gets a " copy" suffix so the
      // uniqueness check on save has something distinct to accept.
      form = { ...formOf(source), name: `${getProfileName(source)} copy` };
    }
    setProfileName(form.name);
    setSelectedTags(form.tags);
    setExtractionPrompt(form.prompt);
    baselineRef.current = draftSignature(form.name, form.tags, form.prompt);

    setError('');
    setDiscardConfirmOpen(false);
  }, [open, openedProfileId, copySourceId]);

  const dirty = draftSignature(profileName, selectedTags, extractionPrompt) !== baselineRef.current;

  // Every way out — Escape, the backdrop, the × and Cancel — comes through
  // here: a dirty draft is only ever lost by choosing Discard.
  const requestClose = () => {
    if (dirty && !loading) {
      setDiscardConfirmOpen(true);
      return;
    }
    onClose(null);
  };

  const handleSubmit = async () => {
    const newName = profileName.trim();
    if (!newName) {
      setError('Profile name is required');
      return;
    }
    const reserved = reservedNameError(newName, 'Profile name');
    if (reserved) {
      setError(reserved);
      return;
    }

    const missing = missingMandatoryAttributes(
      table?.attributeColumns || [], tagsToValueMap(selectedTags));
    if (missing.length > 0) {
      setError(`Missing required attribute${missing.length > 1 ? 's' : ''}: `
        + `${missing.map(formatText).join(', ')}`);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const default_tags = {};
      selectedTags.forEach(tag => {
        default_tags[tag.category] = tag.value;
      });

      // Uniqueness among ACTIVE profiles of this table (normalised; excludes self on edit).
      const profilesRef = collection(db, `users/${companyId}/tables/${tableId}/data_profiles`);
      const snap = await getDocs(profilesRef);
      const clash = snap.docs.some(d => {
        const data = d.data() || {};
        if (isEditing && d.id === initialProfile.id) return false;
        if (data.status === 'Deleted') return false;
        return normalizeProfileName(data.name || d.id) === normalizeProfileName(newName);
      });
      if (clash) {
        setError('A data profile with this name already exists. Please choose another name.');
        setLoading(false);
        return;
      }

      const extraction_prompt = extractionPrompt.trim();

      // The write is not atomic with the check above, so another session can
      // save the same name in the same instant. After the write, read the list
      // back and settle it with profileNameWinner — every writer applies the
      // same rule, so exactly one keeps the name and the rest back out: a
      // create is hard-deleted (it has existed for one round trip and nothing
      // points at it yet), a rename restores its previous name.
      const settleName = async (profileId, backOut) => {
        const after = await getDocs(profilesRef);
        const winner = profileNameWinner(after.docs.map(d => ({ id: d.id, ...d.data() })), newName);
        if (!winner || winner.id === profileId) return true;
        await backOut();
        setError('A data profile with this name was saved by someone else at the same moment. Please choose another name.');
        return false;
      };

      if (isEditing) {
        // Rename and/or tag change — one in-place update of the three fields the
        // editor owns, refused when the document's copy of them is no longer what
        // this editor opened with (another tab saved meanwhile). The backend
        // writes learned stores and statistics into the same document without
        // touching these, so the fields themselves are compared, not a
        // timestamp: an upload finishing must not refuse an edit (M231).
        const ref = doc(db, `users/${companyId}/tables/${tableId}/data_profiles/${initialProfile.id}`);
        const refusal = await runTransaction(db, async (transaction) => {
          const snapshot = await transaction.get(ref);
          if (!snapshot.exists()) return 'This data profile no longer exists.';
          const stored = formOf({ id: initialProfile.id, ...snapshot.data() });
          if (draftSignature(stored.name, stored.tags, stored.prompt) !== baselineRef.current) {
            return CHANGED_SINCE_OPEN;
          }
          await assertCategoriesAvailable(transaction, companyId, Object.keys(default_tags));
          transaction.update(ref, { name: newName, default_tags, extraction_prompt });
          return null;
        });
        if (refusal) {
          setError(refusal);
          return;
        }
        const previousName = getProfileName(initialProfile);
        const renamed = normalizeProfileName(previousName) !== normalizeProfileName(newName);
        if (renamed && !(await settleName(initialProfile.id, () => updateDoc(ref, { name: previousName })))) {
          return;
        }
        onClose({ ...initialProfile, name: newName, default_tags, extraction_prompt });
        return;
      }

      // Copy — the learned stores come from the source doc as it stands right now, not
      // from the (possibly stale) snapshot the sidebar handed us.
      const learned = {};
      if (isCopying) {
        const sourceSnap = await getDoc(
          doc(db, `users/${companyId}/tables/${tableId}/data_profiles/${copySource.id}`));
        if (!sourceSnap.exists()) {
          setError('The source profile no longer exists.');
          setLoading(false);
          return;
        }
        const sourceData = sourceSnap.data() || {};
        COPIED_LEARNED_FIELDS.forEach(field => {
          if (Object.prototype.hasOwnProperty.call(sourceData, field)) {
            learned[field] = sourceData[field];
          }
        });
      }

      // Create — auto-ID; no persisted `id` field. The table is read in the
      // same transaction: one deleted from another tab while this editor was
      // open must not have a new profile created beneath it.
      const created = doc(profilesRef);
      await runTransaction(db, async (transaction) => {
        await assertTableAcceptsWrites(transaction, companyId, tableId);
        await assertCategoriesAvailable(transaction, companyId, Object.keys(default_tags));
        transaction.set(created, {
          name: newName,
          status: 'Active',
          default_tags,
          extraction_prompt,
          created_at: serverTimestamp(),
          ...learned,
        });
      });
      if (!(await settleName(created.id, () => deleteDoc(created)))) return;
      onClose({ id: created.id, name: newName, status: 'Active', default_tags, extraction_prompt });
    } catch (err) {
      console.error('Error saving data profile:', err);
      setError(`Failed to save data profile: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <Dialog 
      open={open} 
      onClose={requestClose} 
      maxWidth="md" 
      fullWidth
      aria-labelledby="data-profile-editor-dialog-title"
    >
      <DialogTitle id="data-profile-editor-dialog-title">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {isEditing
              ? `Edit Data Profile: ${getProfileName(initialProfile)}`
              : isCopying
                ? `Copy Data Profile: ${getProfileName(copySource)}`
                : 'Create New Data Profile'}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {!isEditing && !isCopying && (
              <Button variant="outlined" size="small" onClick={() => setImportOpen(true)}>
                Import Profiles
              </Button>
            )}
            <HelpButton 
              docPath="data-page/using-data-profiles/what-are-data-profiles" 
              tooltip="Data Profiles Documentation"
            />
            <IconButton
              aria-label="close"
              onClick={requestClose}
              sx={{
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <TextField
            label="Profile Name"
            value={profileName}
            onChange={(e) => { setProfileName(e.target.value); setError(''); }}
            fullWidth
            required
            error={error && !profileName.trim()}
            helperText={error && !profileName.trim() ? 'Profile name is required' : ''}
          />
        </Box>

        <Typography variant="subtitle1" gutterBottom>
          Default Attributes
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Select the default attribute values that will be applied to files uploaded with this profile.
        </Typography>

        <Box sx={{ mb: 3 }}>
          <TagSelector
            companyId={companyId}
            selectedTags={selectedTags}
            onTagsChange={(tags) => { setSelectedTags(tags); setError(''); }}
            tableMetadataColumns={attributeColumns}
          />
        </Box>

        <Typography variant="subtitle1" gutterBottom>
          Extraction Prompt
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Optional instructions for the document extraction model, applied to PDF and Word uploads
          made with this profile — on top of any prompt set in the table settings.
        </Typography>
        <Box sx={{ mb: 2 }}>
          <TextField
            multiline
            minRows={3}
            fullWidth
            placeholder="e.g. Loss runs from this broker repeat the column headers under every vessel banner."
            value={extractionPrompt}
            onChange={(e) => { setExtractionPrompt(e.target.value); setError(''); }}
            inputProps={{ maxLength: 4000 }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={requestClose}>Cancel</Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading}
        >
          {loading ? 'Saving...' : (isCopying ? 'Create Copy' : 'Save Profile')}
        </Button>
      </DialogActions>
    </Dialog>

    <Dialog open={discardConfirmOpen} onClose={() => setDiscardConfirmOpen(false)}>
      <DialogTitle>Discard unsaved changes?</DialogTitle>
      <DialogContent>
        <Typography variant="body2">You have unsaved changes. Are you sure you wish to exit?</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDiscardConfirmOpen(false)}>Keep editing</Button>
        <Button color="error" variant="contained" onClick={() => { setDiscardConfirmOpen(false); onClose(null); }}>
          Discard
        </Button>
      </DialogActions>
    </Dialog>

    {/* Import Profiles Dialog */}
    <ImportDataProfilesDialog
      open={importOpen}
      onClose={(result) => {
        setImportOpen(false);
        if (result?.success) {
          onClose(null);
        }
      }}
      companyId={companyId}
      targetTableId={tableId}
      targetTable={table}
    />
    </>
  );
};

export default DataProfileEditor;
